import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../components/ui';
import { AuthShell, authInputCls } from '../components/auth/AuthShell';
import { useAuthStore } from '../store/useAuthStore';

/**
 * Login — email + password sign-in via Cognito InitiateAuth (USER_PASSWORD_AUTH).
 * Direct cognito-idp fetch; on success the ID token's tenant/role claims hydrate
 * the auth store (persisted to localStorage). Auth logic is unchanged — the
 * split-screen brand chrome now lives in the shared <AuthShell> so login and
 * the password-reset page render identically.
 */
export const Route = createFileRoute('/login')({
  component: Login,
});

const POOL_REGION = (import.meta.env.VITE_COGNITO_REGION as string | undefined) ?? 'eu-central-1';
const CLIENT_ID =
  ((import.meta.env.VITE_COGNITO_WEB_CLIENT_ID ??
    import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID) as string | undefined) ?? '';

interface AuthTokens {
  IdToken: string;
  AccessToken: string;
  RefreshToken: string;
  ExpiresIn: number;
  TokenType: 'Bearer';
}

interface CognitoAuthResponse {
  AuthenticationResult?: AuthTokens;
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: Record<string, string>;
}

async function cognitoCall(target: string, body: Record<string, unknown>): Promise<CognitoAuthResponse> {
  const url = `https://cognito-idp.${POOL_REGION}.amazonaws.com/`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await response.json()) as Record<string, unknown>;
  if (!response.ok) {
    const msg = (data.message as string) || (data.__type as string) || `HTTP ${response.status}`;
    throw new Error(msg);
  }
  return data as unknown as CognitoAuthResponse;
}

function initiateAuth(email: string, password: string): Promise<CognitoAuthResponse> {
  return cognitoCall('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: { USERNAME: email, PASSWORD: password },
  });
}

// First-time invited users sign in with a temp password and Cognito answers
// with a NEW_PASSWORD_REQUIRED challenge instead of tokens; this completes it.
function respondToNewPassword(email: string, newPassword: string, session: string): Promise<CognitoAuthResponse> {
  return cognitoCall('RespondToAuthChallenge', {
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: CLIENT_ID,
    Session: session,
    ChallengeResponses: { USERNAME: email, NEW_PASSWORD: newPassword },
  });
}

// The pool policy: min 12 chars, upper + lower + digit + symbol.
function passwordPolicyError(pw: string): string | null {
  if (pw.length < 12) return 'Password must be at least 12 characters.';
  if (!/[A-Z]/.test(pw)) return 'Password must include an uppercase letter.';
  if (!/[a-z]/.test(pw)) return 'Password must include a lowercase letter.';
  if (!/[0-9]/.test(pw)) return 'Password must include a number.';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Password must include a symbol.';
  return null;
}

function decodeIdTokenClaims(idToken: string): Record<string, unknown> {
  const part = idToken.split('.')[1] ?? '';
  return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
}

function Login() {
  const navigate = useNavigate();
  const signIn = useAuthStore((s) => s.signIn);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // First-login "set a new password" step.
  const [challengeSession, setChallengeSession] = useState<string | null>(null);
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  useEffect(() => {
    if (isAuthenticated) void navigate({ to: '/' });
  }, [isAuthenticated, navigate]);

  // Turn a token bundle into an authenticated session. Shared by the normal
  // sign-in path and the first-login new-password path.
  const completeSignIn = (tokens: AuthTokens) => {
    const claims = decodeIdTokenClaims(tokens.IdToken);
    const sub = (claims.sub as string) ?? '';
    const tenantId = String(claims['custom:tenant_id'] ?? '');
    const role = ((claims['custom:role'] as string) ?? 'trainee') as
      | 'trainee' | 'instructor' | 'org_admin' | 'superadmin';
    if (!sub || !tenantId) throw new Error('Token missing sub or custom:tenant_id claim.');
    signIn(
      { userId: sub, email: (claims.email as string) ?? email, tenantId, role },
      {
        idToken: tokens.IdToken,
        accessToken: tokens.AccessToken,
        refreshToken: tokens.RefreshToken,
        expiresAt: Date.now() + (tokens.ExpiresIn ?? 3600) * 1000,
      },
    );
    void navigate({ to: '/' });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!email || !password) {
      setErr('Email and password are required.');
      return;
    }
    if (!CLIENT_ID) {
      setErr('Cognito client ID not configured. Set VITE_COGNITO_WEB_CLIENT_ID in frontend/.env.local.');
      return;
    }
    setLoading(true);
    try {
      const result = await initiateAuth(email, password);
      if (result.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        // First login with a temporary password — ask for a permanent one.
        setChallengeSession(result.Session ?? null);
        setNewPw('');
        setConfirmPw('');
        setLoading(false);
        return;
      }
      if (!result.AuthenticationResult) {
        throw new Error(
          result.ChallengeName
            ? `Additional sign-in step required (${result.ChallengeName}) is not supported here.`
            : 'Sign-in did not return tokens.',
        );
      }
      completeSignIn(result.AuthenticationResult);
    } catch (cause) {
      setLoading(false);
      setErr(cause instanceof Error ? cause.message : 'Sign-in failed');
    }
  };

  const onSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const policy = passwordPolicyError(newPw);
    if (policy) {
      setErr(policy);
      return;
    }
    if (newPw !== confirmPw) {
      setErr('Passwords do not match.');
      return;
    }
    if (!challengeSession) {
      setErr('Your session expired. Please sign in again.');
      setChallengeSession(null);
      return;
    }
    setLoading(true);
    try {
      const result = await respondToNewPassword(email, newPw, challengeSession);
      if (!result.AuthenticationResult) {
        throw new Error('Could not complete password setup. Please sign in again.');
      }
      setChallengeSession(null);
      completeSignIn(result.AuthenticationResult);
    } catch (cause) {
      setLoading(false);
      setErr(cause instanceof Error ? cause.message : 'Could not set the new password');
    }
  };

  const inputCls = authInputCls;

  return (
    <AuthShell>
      <h1 className="font-display text-[26px] font-bold tracking-tight text-txt-bright">
        {challengeSession ? 'Set a new password' : 'Welcome back'}
      </h1>
      <p className="mt-2 text-[14px] leading-relaxed text-txt-2">
        {challengeSession
          ? 'This is your first sign-in. Choose a password to secure your account.'
          : 'Sign in to your cyber range workspace.'}
      </p>

      {challengeSession ? (
        <form onSubmit={onSetNewPassword} className="mt-8 space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-txt">New password</span>
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.currentTarget.value)}
              autoComplete="new-password" placeholder="At least 12 characters" required disabled={loading} className={inputCls} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-txt">Confirm password</span>
            <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.currentTarget.value)}
              autoComplete="new-password" placeholder="Re-enter your new password" required disabled={loading} className={inputCls} />
          </label>
          <p className="text-[12px] leading-relaxed text-txt-muted">
            Use 12+ characters with uppercase, lowercase, a number, and a symbol.
          </p>

          {err ? (
            <div className="flex items-start gap-2 rounded-xl border border-hud-red/40 bg-hud-red/10 px-3 py-2.5">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-hud-red" />
              <span className="text-[12.5px] leading-relaxed text-hud-red">{err}</span>
            </div>
          ) : null}

          <Button type="submit" loading={loading} fullWidth className="mt-1" rightIcon={loading ? undefined : <ArrowRight size={16} />}>
            {loading ? 'Saving…' : 'Set password & sign in'}
          </Button>
        </form>
      ) : (
        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-txt">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value.trim())}
              autoComplete="email" placeholder="you@company.com" required disabled={loading} className={inputCls} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-txt">Password</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.currentTarget.value)}
              autoComplete="current-password" placeholder="Enter your password" required disabled={loading} className={inputCls} />
          </label>
          <div className="flex justify-end -mt-1.5">
            <button
              type="button"
              onClick={() => navigate({ to: '/forgot-password' })}
              className="text-[12.5px] text-txt-muted transition-colors hover:text-txt"
            >
              Forgot password?
            </button>
          </div>

          {err ? (
            <div className="flex items-start gap-2 rounded-xl border border-hud-red/40 bg-hud-red/10 px-3 py-2.5">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-hud-red" />
              <span className="text-[12.5px] leading-relaxed text-hud-red">{err}</span>
            </div>
          ) : null}

          <Button type="submit" loading={loading} fullWidth className="mt-1" rightIcon={loading ? undefined : <ArrowRight size={16} />}>
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
